import React, { useCallback, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { usePlaidLink } from 'react-plaid-link';
import { v4 as uuid } from 'uuid';
import ModalBody from '../Modal/ModalBody';
import BasicModal from '../BasicModal/BasicModal';
import CardButton, {
  CardButtonContentItem,
  CardButtonNote,
} from './components/CardButton';
import { ReactComponent as BankIcon } from '../../assets/images/svg/bank.svg';
import { ReactComponent as CreditCardIcon } from '../../assets/images/svg/credit-card.svg';
import { ReactComponent as InfoIcon } from '../../assets/images/svg/info-ico.svg';
import {
  // getCurrentAccount,
  getCurrentAccountUid,
} from '../../store/selectors/accountSelectors';
import {
  // CHOSEN_ACH_METHOD,
  IS_ADD_ACCOUNT_MODAL_OPEN,
  NEW_ACH_ERROR,
  PLAID_STATUS_MODAL_OPEN,
  PREVENT_SUPPORT_MODAL,
} from '../../constants/sessionStorage';
import { useSessionStorageState } from '../../util/hooks/useSessionStorageState';
import AddCreditDebitCardWizard from '../Modals/CreditCard/AddCreditDebitCardWizard';
import { selectLinkToken } from '../../store/selectors/plaidSelectors';
import { fetchLinkToken } from '../../store/actions/plaid/PlaidActions';
import { retrieveFromSessionStorage } from '../../util/helpers/sessionStorageHelper';
import { selectIsLoadingByActionType } from '../../store/selectors/loadingSelectors';
import { PLAID_LINK_TOKEN_LOADING } from '../../store/actions/plaid/PlaidActionConstants';
import {
  closeWizardContent,
  deleteWizardKey,
  setWizardContent,
} from '../../util/helpers/wizardHelpers';
import {
  fetchBankAccountsByType,
  submitNewAchAccount,
} from '../../store/actions/bankAccount/bankAccountActions';
import StatusModal from '../Modals/StatusModal';
import SectionLoader from '../Loader/SectionLoader';

const AddPaymentMethod = ({
  isOpen,
  onClose,
  postPaidFlow,
  setIsAddPaymentMethodOpen,
  setIsNewCardAdded,
  setIsNewACHAdded,
  setOpen,
  creditCardOnly,
}) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const accountUid = useSelector(getCurrentAccountUid);
  const [isNewAchLoading, setIsNewAchLoading] = useState(false);
  const isLoading =
    useSelector(selectIsLoadingByActionType(PLAID_LINK_TOKEN_LOADING)) ||
    isNewAchLoading;
  const [
    plaidStatusModalOpen,
    setPlaidStatusModalOpen,
  ] = useSessionStorageState(PLAID_STATUS_MODAL_OPEN, false);
  const [newAchError, setNewAchError] = useState(null);
  const [isABADisabled, setIsABADisabled] = useState(false);
  const linkToken = useSelector(selectLinkToken);
  const [isAddingNewCard, setIsAddingNewCard] = useState(false);
  const [chooseAchMethodOpen, setChooseAchMethodOpen] = useSessionStorageState(
    IS_ADD_ACCOUNT_MODAL_OPEN,
    false,
  );

  // const [chosenAchMethod, setChosenAchMethod] = useSessionStorageState(
  //   CHOSEN_ACH_METHOD,
  //   null,
  // );

  useEffect(() => {
    if (accountUid && isOpen && !creditCardOnly) {
      dispatch(fetchLinkToken());
    }
  }, [accountUid, isOpen, creditCardOnly]); // eslint-disable-line

  const onSuccess = useCallback(
    (token, metadata) => {
      const handleApiResponse = (errorMessage, isABADisabled) => {
        const isSupportModalPrevented = retrieveFromSessionStorage(
          PREVENT_SUPPORT_MODAL,
        );
        setIsABADisabled(isABADisabled);

        if (isSupportModalPrevented) {
          deleteWizardKey(IS_ADD_ACCOUNT_MODAL_OPEN);
          setChooseAchMethodOpen(false);
          dispatch(
            fetchBankAccountsByType({
              accountUid,
              type: 'achdeposit',
            }),
          );
        } else {
          setChooseAchMethodOpen(false);
          if (!postPaidFlow) {
            closeWizardContent();
          }
          setNewAchError(errorMessage);
          setPlaidStatusModalOpen(true);
          setWizardContent(NEW_ACH_ERROR, errorMessage);
          setWizardContent(PLAID_STATUS_MODAL_OPEN, true);
          dispatch(
            fetchBankAccountsByType({
              accountUid,
              type: 'achdeposit',
            }),
          );
        }
        setIsNewAchLoading(false);
      };

      const bankAccountIdArray = metadata.accounts.map((account) => account.id);
      const data = {
        PlaidDetails: {
          PublicToken: token,
          BankName: metadata.institution.name,
          BankAccounts: bankAccountIdArray,
        },
        BankAccountType: 'Savings',
        BankAccountUid: uuid(),
        PurposeType: 'AchDeposit',
      };
      setIsNewAchLoading(true);
      dispatch(
        submitNewAchAccount({
          data,
          accountUid,
          handleApiResponse,
          onSuccess: () => {
            setIsNewACHAdded(true);
          },
        }),
      );
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [dispatch, accountUid, setChooseAchMethodOpen],
  );

  const onExit = useCallback((err) => {
    if (err != null && err.error_code === 'INVALID_LINK_TOKEN') {
      dispatch(fetchLinkToken());
    }
  }, []); // eslint-disable-line

  const config = {
    token: linkToken,
    onSuccess,
    onExit,
  };

  const { open } = usePlaidLink(config);

  const BankAccountCardContent = () => (
    <>
      <CardButtonContentItem>
        {t('buyWizard.payment.bankAccountCardItem1')}
      </CardButtonContentItem>
      <CardButtonContentItem>
        {t('buyWizard.payment.bankAccountCardItem2')}
      </CardButtonContentItem>
    </>
  );

  const CreditCardCardContent = () => (
    <>
      <CardButtonContentItem>
        {t('buyWizard.payment.debitAndCreditCardItem1')}
      </CardButtonContentItem>
      <CardButtonContentItem>
        {t('buyWizard.payment.debitAndCreditCardItem2')}
      </CardButtonContentItem>
      {!creditCardOnly && (
        <CardButtonNote>
          <InfoIcon />
          {t('buyWizard.payment.debitAndCreditCardNote')}
        </CardButtonNote>
      )}
    </>
  );

  if (isAddingNewCard) {
    return (
      <AddCreditDebitCardWizard
        isOpen={isAddingNewCard}
        onClose={() => {
          setIsAddingNewCard(false);
          // onClose();
        }}
        postPaidFlow
        setIsNewCardAdded={setIsNewCardAdded}
        setIsNewACHAdded={setIsNewACHAdded}
        goToInitialModal={() => {}}
        avoidClearingStorage
        setIsAddingNewCard={setIsAddingNewCard}
        setOpen={setOpen}
        creditCardOnly={creditCardOnly}
      />
    );
  }

  return (
    <>
      <BasicModal
        title={t('buyWizard.payment.addPaymentMethod')}
        size="sm"
        isOpen={isOpen}
        close={onClose}
        closeIconVisible
        avoidClearingStorage
      >
        <SectionLoader isLoading={isLoading}>
          <ModalBody>
            {!creditCardOnly && (
              <CardButton
                title={t('buyWizard.payment.bankAccount')}
                icon={<BankIcon />}
                tag={t('buyWizard.payment.recommended')}
                onButtonClick={() => open()}
                ribbon={t('buyWizard.payment.savePrecent')}
                content={BankAccountCardContent()}
                isOpen={chooseAchMethodOpen}
              />
            )}
            <CardButton
              title={
                creditCardOnly
                  ? t('buyWizard.payment.creditCardOnly')
                  : t('buyWizard.payment.debitAndCreditCard')
              }
              icon={<CreditCardIcon />}
              onButtonClick={() => {
                setIsAddingNewCard(true);
              }}
              content={CreditCardCardContent()}
            />
          </ModalBody>
        </SectionLoader>
      </BasicModal>

      {newAchError && (
        <StatusModal
          isOpen={plaidStatusModalOpen}
          hasError={newAchError}
          close={() => {
            setPlaidStatusModalOpen(false);
            if (postPaidFlow) {
              setIsAddPaymentMethodOpen(false);
              // setIsAddingNewAch(false);
              setWizardContent(PLAID_STATUS_MODAL_OPEN, false);
              deleteWizardKey(IS_ADD_ACCOUNT_MODAL_OPEN);
            }
          }}
          text={newAchError || t('bankAccounts.newAccountPlaidSuccess')}
          modalTitle={t('achDeposit.chooseAchAddMethod.modalTitle')}
          backButtonText={t('common.ok')}
          onButtonClick={() => {
            if (!postPaidFlow) {
              closeWizardContent();
            }
            setPlaidStatusModalOpen(false);
            if (postPaidFlow) {
              setIsAddPaymentMethodOpen(false);
              // setIsAddingNewAch(false);
              setWizardContent(PLAID_STATUS_MODAL_OPEN, false);
              deleteWizardKey(IS_ADD_ACCOUNT_MODAL_OPEN);
            }
          }}
          isABADisabled={isABADisabled}
        />
      )}
    </>
  );
};

AddPaymentMethod.propTypes = {
  isOpen: PropTypes.func,
  onClose: PropTypes.func,
  postPaidFlow: PropTypes.bool,
  setIsAddPaymentMethodOpen: PropTypes.func,
  setIsNewCardAdded: PropTypes.func,
  setIsNewACHAdded: PropTypes.func,
  setOpen: PropTypes.func,
  creditCardOnly: PropTypes.bool,
};

export default AddPaymentMethod;
